// Field-level validation hook

import { useState, useEffect, useCallback } from 'react'
import { ValidationRule, ValidationResult, ValidationTrigger } from '../types'
import { validateValue } from '../utils'

export function useValidation<T>(
  value: T,
  rules: ValidationRule[],
  trigger: ValidationTrigger = 'onChange'
): ValidationResult & {
  validate: () => Promise<ValidationResult>
  clearError: () => void
} {
  const [validationResult, setValidationResult] = useState<ValidationResult>({
    isValid: true
  })
  const [hasBlurred, setHasBlurred] = useState(false)

  // Manual validation function
  const validate = useCallback(async (): Promise<ValidationResult> => {
    const errors = validateValue(value, rules)
    const result: ValidationResult = {
      isValid: errors.length === 0,
      error: errors.length > 0 ? errors[0] : undefined,
      errors: errors.length > 0 ? errors : undefined
    }
    setValidationResult(result)
    return result
  }, [value, rules])

  // Clear validation error
  const clearError = useCallback(() => {
    setValidationResult({ isValid: true })
  }, [])

  // Auto-validation based on trigger
  useEffect(() => {
    const shouldValidate = 
      trigger === 'onChange' || 
      (trigger === 'onBlur' && hasBlurred) ||
      trigger === 'onSubmit'

    if (shouldValidate && rules.length > 0) {
      validate()
    }
  }, [value, rules, trigger, hasBlurred, validate])

  // Handle blur event for onBlur trigger
  const handleBlur = useCallback(() => {
    setHasBlurred(true)
    if (trigger === 'onBlur') {
      validate()
    }
  }, [trigger, validate])

  return {
    ...validationResult,
    validate,
    clearError
  }
}